import { Head, useForm } from '@inertiajs/react'
import { Building2, Save } from 'lucide-react'
import type { FormEvent } from 'react'

import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { TextArea } from '@/components/ui/TextArea'
import { Label } from '@/components/ui/Label'
import { Button } from '@/components/ui/Button'
import { ImageUpload } from '@/components/ui/ImageUpload'
import { FieldError } from '@/components/feedback/FieldError'
import type { Breadcrumb } from '@/types/shared'

type SchoolForm = {
  name: string | null
  prefectureId: number | null
  league: string | null
  selfIntroduction: string | null
  iconUrl?: string | null
}

type Options = {
  prefectures: Array<{ code: number; name: string }>
}

export type SchoolEditProps = {
  school: SchoolForm
  options: Options
  breadcrumbs: Breadcrumb[]
}

export default function Edit({ school, options, breadcrumbs }: SchoolEditProps) {
  const { data, setData, errors, processing, transform, put } = useForm({
    name: school.name ?? '',
    prefecture_id: school.prefectureId?.toString() ?? '',
    league: school.league ?? '',
    self_introduction: school.selfIntroduction ?? '',
    icon: null as File | null,
  })

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    transform((current) => ({ school: current }))
    put('/school/school', {
      preserveScroll: true,
      onFinish: () => {
        transform((current) => current)
      },
    })
  }

  return (
    <AppShell>
      <Head title="高校情報編集" />
      <PageHeader
        title="高校情報編集"
        description="高校名や所在地などの基本情報を最新の状態に保ちましょう。"
        breadcrumbs={breadcrumbs}
        actions={
          <div className="flex items-center gap-2 text-xs text-white/50">
            <Building2 className="h-4 w-4 text-brand-200" />
            登録情報はチームに公開されます
          </div>
        }
      />

      <form onSubmit={submit} className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Card padding="lg" className="space-y-6">
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-white">基本情報</h2>

            <div className="space-y-2">
              <Label>高校アイコン</Label>
              <ImageUpload
                currentImageUrl={school.iconUrl}
                onImageChange={(file) => setData('icon', file)}
                error={errors.icon}
                label="アイコンを選択"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="school-name">高校名</Label>
              <Input
                id="school-name"
                placeholder="例：〇〇高等学校"
                value={data.name}
                onChange={(event) => setData('name', event.target.value)}
              />
              <FieldError message={errors.name} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="school-prefecture">都道府県</Label>
              <Select
                id="school-prefecture"
                value={data.prefecture_id}
                onChange={(event) => setData('prefecture_id', event.target.value)}
              >
                <option value="">選択してください</option>
                {options.prefectures.map((prefecture) => (
                  <option key={prefecture.code} value={prefecture.code}>
                    {prefecture.name}
                  </option>
                ))}
              </Select>
              <FieldError message={errors.prefecture_id} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="school-league">所属リーグ</Label>
              <Input
                id="school-league"
                placeholder="例：〇〇県リーグ1部"
                value={data.league}
                onChange={(event) => setData('league', event.target.value)}
              />
              <FieldError message={errors.league} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="school-self-introduction">自己紹介</Label>
              <TextArea
                id="school-self-introduction"
                rows={6}
                value={data.self_introduction}
                onChange={(event) => setData('self_introduction', event.target.value)}
                placeholder="学校やサッカー部の特徴、指導方針、選手へのメッセージなどを記入してください。"
              />
              <FieldError message={errors.self_introduction} />
            </div>
          </section>
        </Card>

        <div className="space-y-6">
          <Card padding="lg" className="space-y-4">
            <h3 className="text-lg font-semibold text-white">公開情報について</h3>
            <p className="text-sm text-white/70">
              登録された高校情報は、選手とチームが閲覧できる公開情報として表示されます。最新の内容に更新することで、信頼感の向上とスカウティングの精度向上につながります。
            </p>
          </Card>

          <Card padding="lg" className="space-y-4">
            <Button type="submit" className="w-full" disabled={processing}>
              <Save className="mr-2 h-4 w-4" />
              情報を更新する
            </Button>
            <p className="text-center text-xs text-white/50">
              更新後すぐに他のメンバーへ反映されます。
            </p>
          </Card>
        </div>
      </form>
    </AppShell>
  )
}
